import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'

//evertel
import {
    Modal,
    ModalBody,
    ModalHeader,
    ModalFooter,
    Row,
    Col,
    Input,
    Textarea,
    Label,
    Button,
    Icon,
    useUI
} from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { Api } from '@evertel/api'
import { AppService } from '@evertel/web/core'
import { SessionService } from '@evertel/session/SessionService'

interface ContactSupportModalProps {
    isOpen: boolean
    onClose: () => void
}

const ContactSupportModal: React.FC<ContactSupportModalProps> = observer(({
    isOpen,
    onClose
}) => {
    const appService = useService(AppService, [])
    const api = useService(Api, [])
    const sessionService = useService(SessionService, [])

    const { addToast } = useUI()

    const [subject, setSubject] = useState('')
    const [description, setDescription] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const handleSubmit = async () => {
        if (!subject.trim()) return

        setIsLoading(true)
        setError(null)

        try {
            const msg = description.trim()
                ? `${subject}:\n\n${description}`
                : subject

            const data = {
                page: window.location.pathname,
                timestamp: new Date().toISOString(),
                userInfo: { id: sessionService.currentUserId },
                ...await appService.debugInfo()
            }

            await api.Routes.App.postFeedback({ msg, data })

            addToast({
                title: 'Success',
                message: 'Your feedback has been delivered successfully',
                color: 'success'
            })
            setSubject('')
            setDescription('')
            onClose()

        } catch (e: unknown) {
            console.error('Failed to submit feedback:', e)
            setError('Failed to submit feedback. Please try again.')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Modal
            visible={isOpen}
            onClose={onClose}
            size="lg"
            className="p-5"
        >
            <ModalHeader
                title="Contact Technical Support"
                subtitle="Let us know how we can help."
                closeButton
            />

            <ModalBody>
                {error && (
                    <Row className="mb-4">
                        <Col>
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        </Col>
                    </Row>
                )}

                <Row className="mb-5">
                    <Col>
                        <Label
                            htmlFor="issue-input"
                            text="What do you need help with?"
                            className="mb-3"
                            bold
                        />
                        <Input
                            id="issue-input"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="Describe your issue"
                        />
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <Label
                            htmlFor="additional-info-input"
                            text="Additional Information"
                            optional
                            className="mb-3"
                            bold
                        />
                        <Textarea
                            id="additional-info-input"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Add more details about your issue"
                            rows={5}
                        />
                    </Col>
                </Row>
                <Row align="end" className="mt-4">
                    {isLoading && (
                        <div
                            className="visually-hidden"
                            role="status"
                            aria-live="polite"
                        >
                            Submitting your support request, please wait
                        </div>
                    )}
                    <Button
                        color="primary"
                        onClick={handleSubmit}
                        loading={isLoading}
                        disabled={!subject.trim()}
                        className="ms-auto"
                    >
                        Submit
                    </Button>
                </Row>
            </ModalBody>

            <ModalFooter className="border-top pt-3 pb-3">
                <div className="d-flex align-items-center justify-content-around flex-wrap w-100">
                    <div>
                        <Icon name="life-ring" color="primary" className="me-2" />
                        <a
                            href="https://gethelp.genasys.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-decoration-none text-primary fw-bold"
                            aria-label="Open Help Center in a new tab"
                        >
                            Help Center
                            <span className="visually-hidden"> (opens in new tab)</span>
                        </a>
                    </div>

                    <span className="text-muted mx-2" aria-hidden="true">|</span>

                    <a
                        href="mailto:EvertelSupport@genasys.com"
                        className="text-decoration-none text-primary fw-bold"
                        aria-label="Send email to Evertel support"
                    >
                        EvertelSupport@genasys.com
                    </a>

                    <span className="text-muted mx-2" aria-hidden="true">|</span>

                    <span className="text-muted fw-bold">
                        <a
                            href="tel:+16193233879"
                            className="text-decoration-none text-primary fw-bold ms-1"
                            aria-label="Call support for critical issues at +1-619-323-3879"
                        >
                            For Critical Issues: +1-619-323-3879
                        </a>
                    </span>
                </div>
            </ModalFooter>
        </Modal>
    )
})

export { ContactSupportModal }
